<template>
  <FormCard
    :title="$t('order_creation.details_step.location_title')"
    data-testid="order-details-location-card"
    :is-open="isOpen"
    :completed="completed"
    :has-error="hasError"
    :show-chevron="true"
    :background-image-url="locationMapUrl"
    :show-form-icons-on-open="false"
    @toggle="emit('toggle')"
    @enter-pressed="emit('enter-pressed')">
    <template #description>
      <div v-if="!isOpen" class="location-summary">
        <div>
          <p class="summary-title">{{ order.workplace_location }}</p>
          <p
            class="summary-subtitle"
            v-if="order.workplace_address && order.workplace_address !== order.workplace_location">
            {{ order.workplace_address }}
          </p>
        </div>
      </div>
    </template>
    <template #pre-form-content>
      <div v-if="locationMapUrl" class="map-preview">
        <img :src="locationMapUrl" alt="Map Preview" />
      </div>
    </template>
    <template #form-content>
      <label v-if="order.company_city || order.company_street" class="toggle" style="margin-bottom: 20px">
        <LeadInput v-model="useCompanyAddress" type="switch" class="w-fit align-self-start"> </LeadInput>
        <div class="d-flex flex-column">
          <p class="mb-0 switch-label">{{ $t('order_creation.details_step.use_company_address') }}</p>
          <p class="switch-hint">
            {{ $t('order_creation.details_step.location_hint') }}
          </p>
        </div>
      </label>

      <AddressSelector
        v-model="order.workplace_address"
        data-testid="order-details-location-selector"
        :label="$t('order_creation.details_step.address')"
        :display-initials="true"
        @updated="handleLocationUpdate"
        :error="error" />
      <div class="form-actions">
        <LeadButton
          data-testid="order-details-location-save-button"
          variant="secondary"
          @click="emit('save')"
          :loading="saving"
          >{{ $t('btn.save_and_continue') }}
          <template #icon>
            <IconCheck :size="16" stroke-width="2" />
          </template>
        </LeadButton>
      </div>
    </template>
  </FormCard>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { IconCheck } from '@tabler/icons-vue';
import FormCard from '@/components/orders/form_cards/FormCard.vue';
import LeadInput from '@/components/form/LeadInput.vue';
import LeadButton from '@/components/generic/LeadButton.vue';
import AddressSelector from '@/components/common/AddressSelector.vue';
import { useOrderCreationStore } from '@/stores/orderCreation';
import { useUserStore } from '@/stores/user';
import { request } from '@/api/client';
import type { IGoogleAddress } from '@/types/address';

withDefaults(
  defineProps<{
    isOpen: boolean;
    completed: boolean;
    hasError: boolean;
    error?: string;
    saving?: boolean;
  }>(),
  {
    saving: false,
  }
);

const emit = defineEmits<{
  toggle: [];
  save: [];
  'enter-pressed': [];
  'map-loaded': [];
}>();

const store = useOrderCreationStore();
const userStore = useUserStore();
const { order } = storeToRefs(store);
const { googleMapsStaticApiKey } = storeToRefs(userStore);

const locationMapUrl = ref<string | null>(null);

const checkIsCompanyAddress = () => {
  if (!order.value || !order.value.workplace_city) return false;

  const companyData = order.value.company?.data;
  const cCity = order.value.company_city || companyData?.address_city;
  const cStreet = order.value.company_street || companyData?.address_street;

  if (!cCity) return false;

  const cityMatches = order.value.workplace_city === cCity;
  const streetMatches = cStreet ? order.value.workplace_address?.includes(cStreet) : true;

  return cityMatches && streetMatches;
};

const useCompanyAddress = ref(checkIsCompanyAddress());

const getZoomLevelForAddress = (address: IGoogleAddress): number => {
  const types = address.types || [];

  if (types.includes('street_address') || types.includes('premise')) {
    return 15;
  }
  if (types.includes('locality')) {
    return 12;
  }
  if (types.includes('administrative_area_level_1')) {
    return 8;
  }
  if (types.includes('country')) {
    return 5;
  }
  return 14;
};

const generateLocationMapUrl = (address: IGoogleAddress) => {
  if (!address || !address.formatted_address) return null;

  const baseUrl = 'https://maps.googleapis.com/maps/api/staticmap';
  const size = '600x300';
  const scale = 2; // for higher resolution on retina displays
  const key = googleMapsStaticApiKey.value || '';

  const styles = [
    'style=feature:administrative|element:geometry|visibility:off',
    'style=feature:administrative.land_parcel|visibility:off',
    'style=feature:administrative.neighborhood|visibility:off',
    'style=feature:poi|visibility:off',
    'style=feature:road|element:labels.icon|visibility:off',
    'style=feature:road.arterial|element:labels|visibility:off',
    'style=feature:road.highway|element:labels|visibility:off',
    'style=feature:road.local|visibility:off',
    'style=feature:transit|visibility:off',
    'style=feature:water|element:geometry|color:0xd6ebf2',
    'style=feature:water|element:labels.text|visibility:off',
    'style=feature:landscape.natural|element:geometry.fill|color:0xeaf2e5',
    'style=feature:landscape.man_made|element:geometry.fill|color:0xf7f7f7',
  ].join('&');

  const zoom = getZoomLevelForAddress(address);
  const query = encodeURIComponent(address.formatted_address);

  return `${baseUrl}?center=${query}&zoom=${zoom}&size=${size}&scale=${scale}&maptype=roadmap&key=${key}&${styles}`;
};

/**
 * Maps Google address components to order workplace fields.
 * If `overwriteLocation` is true, workplace_location is always updated (user-initiated change).
 * If false, only empty fields are filled (initialization from existing data).
 */
const mapAddressToOrder = (address: IGoogleAddress, overwriteLocation = false) => {
  const components = address.address_components || [];

  if (overwriteLocation || !order.value.workplace_address) {
    order.value.workplace_address = address.formatted_address;
  }

  const city = components.find((c) => c.types.includes('locality'))?.long_name;
  const country = components.find((c) => c.types.includes('country'))?.long_name;
  const zip = components.find((c) => c.types.includes('postal_code'))?.long_name;

  if (overwriteLocation || !order.value.workplace_location) {
    order.value.workplace_location = city ? `${city}, ${country}` : address.formatted_address;
  }
  order.value.workplace_city = city || '';
  order.value.workplace_country = country || '';
  order.value.workplace_zip = zip || undefined;
};

const fetchMapImage = async (address: IGoogleAddress) => {
  const mapUrl = generateLocationMapUrl(address);
  if (!mapUrl) {
    locationMapUrl.value = null;
    return;
  }
  try {
    const mapResponse = await fetch(mapUrl);
    if (!mapResponse.ok) throw new Error('Failed to fetch map image');
    const imageBlob = await mapResponse.blob();
    locationMapUrl.value = URL.createObjectURL(imageBlob);
  } catch (error) {
    console.error('Error fetching map image:', error);
    locationMapUrl.value = null;
  }
};

const geocodeAndSetMapUrl = async (addressString: string, overwriteLocation = false) => {
  if (!addressString) {
    locationMapUrl.value = null;
    return;
  }

  try {
    const response: { data: { results: IGoogleAddress[] } } = await request(
      'POST',
      '/v1/account/google-maps/geo-coding',
      undefined,
      { address: addressString }
    );
    const address = response.data.results?.[0];

    if (address) {
      mapAddressToOrder(address, overwriteLocation);
      await fetchMapImage(address);
    } else {
      locationMapUrl.value = null;
    }
  } catch (err) {
    console.error('Error geocoding address:', err);
    locationMapUrl.value = null;
  }
};

const handleLocationUpdate = async (address: IGoogleAddress | null) => {
  if (!address) {
    order.value.workplace_address = '';
    order.value.workplace_location = '';
    order.value.workplace_city = '';
    order.value.workplace_country = '';
    order.value.workplace_zip = undefined;
    locationMapUrl.value = null;
    return;
  }

  // User explicitly selected an address — overwrite all fields including location
  mapAddressToOrder(address, true);
  await fetchMapImage(address);
};

// Resolved once the initial geocoding attempt completes (success or failure).
// DetailsStep awaits this before creating the draft so location fields are in
// the store before the server response is merged back.
let resolveGeocodingReady!: () => void;
const geocodingReady = new Promise<void>((resolve) => {
  resolveGeocodingReady = resolve;
});

onMounted(() => {
  const initialAddress = order.value.workplace_address || order.value.workplace_location;
  const work = initialAddress ? geocodeAndSetMapUrl(initialAddress) : Promise.resolve();
  work.finally(() => {
    resolveGeocodingReady();
    emit('map-loaded');
  });
});

defineExpose({ geocodingReady });

// if the company changes, reset the useCompanyAddress toggle
watch(
  () => order.value.company_id,
  () => {
    useCompanyAddress.value = false;
  }
);

watch(useCompanyAddress, (isUsingCompanyAddress) => {
  if (isUsingCompanyAddress) {
    const companyData = order.value.company?.data;
    const city = order.value.company_city || companyData?.address_city;
    const street = order.value.company_street || companyData?.address_street;
    const country = order.value.company_country || companyData?.address_country;
    const zip = order.value.company_zip || companyData?.address_zip;

    const addressString = [street, zip, city, country].filter(Boolean).join(', ');

    if (addressString) {
      geocodeAndSetMapUrl(addressString, true);
    }
  }
});
</script>

<style scoped lang="scss">
* {
  font-family: var(--font-brand);
}

.summary-title {
  font-weight: 400;
  font-size: 14px;
  color: #101828;
  margin: 0;
}

.summary-subtitle {
  font-size: 13px;
  color: #8e939d;
  margin: 0;
}

.location-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
  position: relative;
}

.toggle {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-top: 10px;
  cursor: pointer;
}

.switch-label {
  font-weight: 600;
}

.switch-hint {
  font-size: 13px;
  color: #8e939d;
}

:deep(.form-check-input) {
  width: 48px !important;
  height: 24px !important;
  background-size: 22px;
}

:deep(.form-check-input:checked) {
  background-color: var(--tblr-primary);
}

.map-preview {
  width: 100%;
  height: 220px;
  margin-bottom: 16px;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0px 0px 12.2px 0px #00000021;
  border: 1px solid #00000026;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.form-actions {
  margin-top: 20px;
  display: flex;
  justify-content: flex-start;
}
</style>
